import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Branding, DEFAULT_BRANDING, fetchCurrentBranding } from '../services/branding';

interface Ctx {
  branding: Branding;
  refreshBranding: () => void;
}
export const BrandingContext = createContext<Ctx>({ branding: DEFAULT_BRANDING, refreshBranding: () => {} });

/**
 * 把机构主色注入 CSS 变量。color 非空时覆盖 --brand-primary 及其派生:
 *   - --brand-primary-2  渐变终点(主色调亮,用 color-mix 派生)
 *   - --brand-primary-shadow  阴影色(主色 + 透明度)
 * color 为空时移除三者,回退 index.css :root 的默认青绿。
 */
function applyBrandColor(color: string | null) {
  const root = document.documentElement;
  if (color) {
    root.style.setProperty('--brand-primary', color);
    // 渐变终点:主色与白色混合 35% → 更亮的同色系,保持渐变层次。
    root.style.setProperty('--brand-primary-2', `color-mix(in srgb, ${color} 65%, white)`);
    // 阴影:主色 20% 不透明度。
    root.style.setProperty('--brand-primary-shadow', `color-mix(in srgb, ${color} 20%, transparent)`);
  } else {
    root.style.removeProperty('--brand-primary');
    root.style.removeProperty('--brand-primary-2');
    root.style.removeProperty('--brand-primary-shadow');
  }
}

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);

  const refreshBranding = useCallback(async () => {
    if (!localStorage.getItem('admin_token')) {
      setBranding(DEFAULT_BRANDING);
      applyBrandColor(null);
      return;
    }
    const b = await fetchCurrentBranding();
    setBranding(b);
    applyBrandColor(b.brandColor);
  }, []);

  useEffect(() => { refreshBranding(); }, [refreshBranding]);

  useEffect(() => {
    const onChange = () => { refreshBranding(); };
    window.addEventListener('acting-org-changed', onChange);
    return () => window.removeEventListener('acting-org-changed', onChange);
  }, [refreshBranding]);

  return (
    <BrandingContext.Provider value={{ branding, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
